import React, { useState, useEffect } from "react";
import {
    X,
    Zap,
    Loader2,
    AlertCircle,
    TrendingUp,
    Copy,
    ChevronDown,
    ChevronUp,
} from "lucide-react";
import { MTEngine } from "@/lib/cat/mtEngine";
import GoogleTranslateProvider from "@/lib/cat/googleTranslate";
import DeepLProvider from "@/lib/cat/deeplTranslate";
import type { SuggestionSource } from "@/lib/cat/segmentAnalytics";
import MyMemoryProvider from "@/lib/cat/myMemoryTranslate";

interface MTSuggestionsPanelProps {
    isOpen: boolean;
    onClose: () => void;
    sourceText: string;
    sourceLang: string;
    targetLang: string;
    onInsert: (text: string, source?: SuggestionSource) => void;
    onBatchTranslate?: () => void;
    defaultProvider?: string;
}

// Providers that are never run automatically. They are charged to the user's plan
// allowance, so they only run when the user asks (see "Get Google suggestion").
const GOOGLE_NAME = "Google Translate";
const ON_DEMAND_PROVIDERS = new Set([GOOGLE_NAME]);

interface MTResult {
    provider: string;
    translation: string;
    confidence: number;
    error?: string;
}

const MTSuggestionsPanel: React.FC<MTSuggestionsPanelProps> = ({
    isOpen,
    onClose,
    sourceText,
    sourceLang,
    targetLang,
    onInsert,
    onBatchTranslate,
    defaultProvider,
}) => {
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState<MTResult[]>([]);
    const [autoTranslate, setAutoTranslate] = useState(false);
    const [showUsage, setShowUsage] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);

    const [enabledProviders, setEnabledProviders] = useState<
        Record<string, boolean>
    >(() => {
        try {
            const stored = localStorage.getItem("translyx_mt_providers");
            return stored
                ? JSON.parse(stored)
                : {
                      DeepL: true,
                      "MyMemory (Free)": true,
                  };
        } catch {
            return {
                DeepL: true,
                "MyMemory (Free)": true,
            };
        }
    });

    const toggleProvider = (name: string) => {
        setEnabledProviders((prev) => {
            const updated = { ...prev, [name]: !prev[name] };
            localStorage.setItem(
                "translyx_mt_providers",
                JSON.stringify(updated),
            );
            return updated;
        });
    };

    const [mtEngine] = useState(() => {
        const engine = new MTEngine();
        engine.registerProvider(new MyMemoryProvider());
        // Google and DeepL are reached through our API, which holds the keys.
        engine.registerProvider(new GoogleTranslateProvider());
        engine.registerProvider(new DeepLProvider());
        return engine;
    });

    useEffect(() => {
        if (autoTranslate && sourceText && isOpen) handleTranslate();
    }, [sourceText, autoTranslate, isOpen]);

    const handleTranslate = async () => {
        if (!sourceText.trim()) return;
        setLoading(true);
        setResults([]);
        try {
            const allProviders = mtEngine.getProviders();
            // MyMemory (free) and DeepL run automatically. Google never does, whatever
            // an older saved setting says.
            const activeProviders = allProviders.filter(
                (p) =>
                    !ON_DEMAND_PROVIDERS.has(p.getName()) &&
                    enabledProviders[p.getName()] !== false,
            );
            const translations = await Promise.all(
                activeProviders.map(async (provider) => {
                    try {
                        const result = await provider.translate(
                            sourceText,
                            sourceLang,
                            targetLang,
                        );
                        return {
                            provider: provider.getName(),
                            translation: result,
                            confidence: 85,
                        };
                    } catch (error) {
                        return {
                            provider: provider.getName(),
                            translation: "",
                            confidence: 0,
                            error:
                                error instanceof Error
                                    ? error.message
                                    : "Failed",
                        };
                    }
                }),
            );
            setResults(translations);
        } catch (error) {
            setResults([
                {
                    provider: "Error",
                    translation: "",
                    confidence: 0,
                    error:
                        error instanceof Error
                            ? error.message
                            : "Translation failed",
                },
            ]);
        } finally {
            setLoading(false);
        }
    };

    // Google is only called, and only charged, when the user clicks the button.
    const handleGoogleSuggestion = async () => {
        if (!sourceText.trim()) return;
        const google = mtEngine
            .getProviders()
            .find((p) => p.getName() === GOOGLE_NAME);
        if (!google) return;
        setGoogleLoading(true);
        let result: MTResult;
        try {
            const translation = await google.translate(
                sourceText,
                sourceLang,
                targetLang,
            );
            result = { provider: GOOGLE_NAME, translation, confidence: 85 };
        } catch (error) {
            result = {
                provider: GOOGLE_NAME,
                translation: "",
                confidence: 0,
                error: error instanceof Error ? error.message : "Failed",
            };
        }
        setResults((prev) => [
            ...prev.filter((r) => r.provider !== GOOGLE_NAME),
            result,
        ]);
        setGoogleLoading(false);
    };

    const getConfidenceBadge = (confidence: number) => {
        if (confidence >= 90)
            return (
                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full font-medium">
                    High ({confidence}%)
                </span>
            );
        if (confidence >= 75)
            return (
                <span className="px-2 py-0.5 bg-gold-100 text-gold-700 text-xs rounded-full font-medium">
                    Medium ({confidence}%)
                </span>
            );
        return (
            <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full font-medium">
                Low ({confidence}%)
            </span>
        );
    };

    const getUsageStats = () => {
        return mtEngine.getProviders().map((provider) => {
            const usage = provider.getUsage();
            const limit = provider.getMonthlyLimit();
            const percentage = Math.round((usage / limit) * 100);
            const color =
                percentage > 80
                    ? "bg-red-500"
                    : percentage > 60
                      ? "bg-gold-500"
                      : "bg-emerald-500";
            return {
                name: provider.getName(),
                usage,
                limit,
                percentage,
                color,
            };
        });
    };

    if (!isOpen) return null;

    const sortedResults = [...results].sort((a, b) => {
        if (defaultProvider) {
            const aMatch = a.provider
                .toLowerCase()
                .includes(defaultProvider.toLowerCase());
            const bMatch = b.provider
                .toLowerCase()
                .includes(defaultProvider.toLowerCase());
            if (aMatch && !bMatch) return -1;
            if (!aMatch && bMatch) return 1;
        }
        return 0;
    });

    return (
        <div className="fixed right-0 top-0 h-full w-96 bg-white border-l border-gray-200 shadow-xl z-50 flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-slate-900">
                <div className="flex items-center space-x-2">
                    <div className="p-1.5 bg-gold-500 rounded-md">
                        <Zap className="h-4 w-4 text-white" />
                    </div>
                    <h3 className="text-base font-bold text-white">
                        Machine Translation
                    </h3>
                </div>
                <button
                    onClick={onClose}
                    className="p-1 rounded-lg hover:bg-slate-700 text-gray-100 hover:text-white transition-colors"
                >
                    <X className="h-5 w-5" />
                </button>
            </div>
            <div className="px-4 py-3 border-b border-slate-700 bg-slate-800 space-y-3">
                <div className="flex items-center justify-between">
                    <button
                        onClick={handleTranslate}
                        disabled={!sourceText.trim() || loading}
                        className="flex items-center gap-2 px-4 py-2 bg-gold-500 hover:bg-gold-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors shadow-sm"
                    >
                        {loading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Zap className="h-4 w-4" />
                        )}
                        {loading ? "Translating..." : "Get Translations"}
                    </button>
                    <label className="flex items-center space-x-2 text-sm cursor-pointer">
                        <input
                            type="checkbox"
                            checked={autoTranslate}
                            onChange={(e) => setAutoTranslate(e.target.checked)}
                            className="rounded border-gray-300 text-gold-500 focus:ring-gold-400"
                        />
                        <span className="text-gray-100 font-medium">
                            Auto-translate
                        </span>
                    </label>
                </div>
                {/* Provider toggles */}
                <div className="flex items-center gap-2 flex-wrap">
                    {mtEngine
                        .getProviders()
                        .filter((p) => !ON_DEMAND_PROVIDERS.has(p.getName()))
                        .map((provider) => {
                        const name = provider.getName();
                        const enabled = enabledProviders[name] !== false;
                        const isDeepL = name.toLowerCase().includes("deepl");
                        return (
                            <button
                                key={name}
                                onClick={() => toggleProvider(name)}
                                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition-all ${
                                    enabled
                                        ? isDeepL
                                            ? "bg-blue-600 border-blue-500 text-white"
                                            : "bg-slate-600 border-slate-500 text-white"
                                        : "bg-slate-800 border-slate-700 text-slate-500 line-through"
                                }`}
                                title={
                                    enabled
                                        ? `Disable ${name}`
                                        : `Enable ${name}`
                                }
                            >
                                {isDeepL
                                    ? "⚡"
                                    : name.toLowerCase().includes("google")
                                      ? "🔍"
                                      : "🌐"}
                                {isDeepL
                                    ? "DeepL"
                                    : name.toLowerCase().includes("google")
                                      ? "Google"
                                      : "MyMemory"}
                            </button>
                        );
                    })}
                </div>

                <div>
                    <button
                        onClick={handleGoogleSuggestion}
                        disabled={!sourceText.trim() || googleLoading}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors border border-slate-600"
                    >
                        {googleLoading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <span aria-hidden="true">🔍</span>
                        )}
                        {googleLoading
                            ? "Getting suggestion..."
                            : "Get Google suggestion"}
                    </button>
                    <p className="text-xs text-slate-400 mt-1">
                        Uses your plan&apos;s translation allowance.
                    </p>
                </div>

                {onBatchTranslate && (
                    <button
                        onClick={onBatchTranslate}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium rounded-lg transition-colors border border-slate-700"
                    >
                        <Zap className="h-4 w-4 text-gold-400" />
                        Batch Translate All Segments
                    </button>
                )}
                {sourceText && (
                    <div className="bg-white border border-gray-200 rounded-lg p-3">
                        <div className="text-xs font-bold text-gray-800 uppercase tracking-wider mb-1">
                            Source Text:
                        </div>
                        <div className="text-sm text-gray-700 line-clamp-3">
                            {sourceText}
                        </div>
                    </div>
                )}
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-white">
                {results.length === 0 && !loading && (
                    <div className="text-center py-12">
                        <div className="w-12 h-12 bg-gold-50 rounded-full flex items-center justify-center mx-auto mb-3">
                            <Zap className="h-6 w-6 text-gold-400" />
                        </div>
                        <p className="text-gray-700 text-sm">
                            Click "Get Translations" for DeepL and MyMemory suggestions
                        </p>
                    </div>
                )}
                {sortedResults.map((result, index) => (
                    <div
                        key={index}
                        className="bg-white border border-gray-200 hover:border-gold-400 rounded-lg p-4 transition-colors shadow-sm"
                    >
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                                <span className="font-bold text-gray-900 text-sm">
                                    {result.provider}
                                </span>
                                {defaultProvider &&
                                    result.provider
                                        .toLowerCase()
                                        .includes(
                                            defaultProvider.toLowerCase(),
                                        ) && (
                                        <span className="px-1.5 py-0.5 bg-gold-100 text-gold-700 border border-gold-300 rounded text-xs font-semibold">
                                            Default
                                        </span>
                                    )}
                                {!result.error &&
                                    getConfidenceBadge(result.confidence)}
                            </div>
                        </div>
                        {result.error ? (
                            <div className="flex items-start space-x-2 text-red-500 text-sm">
                                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                <span>{result.error}</span>
                            </div>
                        ) : (
                            <>
                                <p className="text-gray-700 text-sm mb-3 whitespace-pre-wrap leading-relaxed">
                                    {result.translation}
                                </p>
                                <div className="flex justify-end">
                                    <button
                                        onClick={() =>
                                            onInsert(result.translation, {
                                                kind: "mt",
                                                provider: result.provider,
                                            })
                                        }
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-gold-500 text-white text-xs font-medium rounded-lg transition-colors"
                                    >
                                        <Copy className="h-3 w-3" />
                                        Insert
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                ))}
            </div>
            <div className="border-t border-gray-200">
                <button
                    onClick={() => setShowUsage(!showUsage)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                    <div className="flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-gold-500" />
                        <span className="text-sm font-bold text-gray-800">
                            Monthly Usage
                        </span>
                    </div>
                    {showUsage ? (
                        <ChevronDown className="h-4 w-4 text-gray-700" />
                    ) : (
                        <ChevronUp className="h-4 w-4 text-gray-700" />
                    )}
                </button>
                {showUsage && (
                    <div className="px-4 pb-4 pt-2 bg-gray-50 space-y-3 border-t border-gray-100">
                        {getUsageStats().map((stat, index) => (
                            <div key={index}>
                                <div className="flex justify-between text-xs text-gray-600 mb-1">
                                    <span className="font-medium text-slate-700">
                                        {stat.name}
                                    </span>
                                    <span>
                                        {stat.usage.toLocaleString()} /{" "}
                                        {stat.limit.toLocaleString()} chars
                                    </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-1.5">
                                    <div
                                        className={`${stat.color} h-1.5 rounded-full transition-all duration-300`}
                                        style={{
                                            width: `${Math.min(stat.percentage, 100)}%`,
                                        }}
                                    />
                                </div>
                                <div className="text-right text-xs text-gray-700 mt-0.5">
                                    {stat.percentage}% used
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <div className="border-t border-gray-200 px-4 py-3 bg-slate-900">
                <div className="flex items-start space-x-2">
                    <Zap className="h-4 w-4 text-gold-400 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-gray-100">
                        <p className="font-semibold text-white mb-1">Tips:</p>
                        <ul className="list-disc list-inside space-y-0.5">
                            <li>Use TM first (free, instant matches)</li>
                            <li>DeepL suggestions use your plan allowance</li>
                            <li>Google only runs when you ask for it</li>
                            <li>Review and edit MT suggestions</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MTSuggestionsPanel;
